import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CtaSection from '../../components/feature/CtaSection';
import { newsData, faqData } from '../../mocks/home-data';
import { useSEO, generateMedicalClinicSchema, generateFAQPageSchema } from '../../utils/seo';

export default function Home() {
  useSEO({
    title: '岡山市の小児科クリニック｜おかやま小児科クリニック｜予防接種・乳幼児健診',
    description:
      '岡山市北区のおかやま小児科クリニックは、25年の実績を持つ小児科専門医が、お子様の健康を笑顔で守ります。一般診療、予防接種、乳幼児健診、アレルギー疾患まで幅広く対応。岡山市内・倉敷市・総社市・赤磐市からアクセス良好。駐車場完備で通いやすい小児科です。',
    keywords:
      '岡山市,小児科,クリニック,予防接種,乳幼児健診,アレルギー,子ども,北区,倉敷市,総社市,赤磐市',
    canonicalPath: '/',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateMedicalClinicSchema(),
        generateFAQPageSchema(
          faqData.map((faq) => ({
            question: faq.question,
            answer: faq.answer,
          }))
        ),
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/945e58ee62b75a7ccdb8034b586aa738.jpeg"
            alt="おかやま小児科クリニック"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="absolute inset-0 flex items-center justify-end">
          <div className="text-right text-white px-6 sm:px-10 lg:px-20 max-w-3xl" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.4)' }}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl font-bold mb-4 sm:mb-6 leading-tight">
              岡山市のお子様の健康を<br />笑顔で守る小児科クリニック
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-8 sm:mb-12 leading-relaxed">
              地域に根ざした信頼の小児医療で
              <br className="sm:hidden" />
              お子様の成長を見守ります
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-end items-end sm:items-center">
              <Link
                to="/contact"
                className="w-full sm:w-auto bg-[#F48FB1] hover:bg-[#EC407A] text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                style={{ textShadow: 'none' }}
              >
                診療予約・お問い合わせ
              </Link>
              <Link
                to="/about"
                className="w-full sm:w-auto bg-white/90 hover:bg-white text-[#F48FB1] px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                style={{ textShadow: 'none' }}
              >
                当院について
              </Link>
            </div>
          </div>
        </div>

        {/* Curved bottom - concave arch (curves upward into the image) */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
            <path d="M0,120 L0,0 Q720,120 1440,0 L1440,120 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              おかやま小児科クリニックの特徴
            </h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">岡山市で選ばれる理由</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gradient-to-br from-[#FCE4EC] to-[#FFF0F5] p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#F48FB1] rounded-full flex items-center justify-center mb-4 sm:mb-6 mx-auto">
                <i className="ri-heart-pulse-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">
                経験豊富な小児科専門医
              </h4>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                25年以上の実績を持つ小児科専門医が、お子様一人ひとりに寄り添った丁寧な診療を行います
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#FFFDE7] to-[#FFF8E1] p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FFD54F] rounded-full flex items-center justify-center mb-4 sm:mb-6 mx-auto">
                <i className="ri-home-smile-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">
                お子様に優しい空間
              </h4>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                明るく清潔な院内で、お子様がリラックスできる工夫がいっぱい。待ち時間も楽しく過ごせます
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#EDE7F6] to-[#F3E5F5] p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#A78BFA] rounded-full flex items-center justify-center mb-4 sm:mb-6 mx-auto">
                <i className="ri-map-pin-line text-3xl sm:text-4xl text-white"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">
                岡山市北区の好立地
              </h4>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-center">
                アクセス良好で駐車場完備。岡山市内はもちろん、近隣市町村からも通いやすい立地です
              </p>
            </div>
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Link
              to="/about"
              className="inline-block bg-[#F48FB1] hover:bg-[#EC407A] text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              詳しく見る
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FFFDE7]/60 via-[#FCE4EC]/40 to-[#EDE7F6]/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">診療内容</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              岡山市のお子様の健康をトータルサポート
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: 'ri-stethoscope-line',
                title: '一般小児科診療',
                desc: '風邪、発熱、腹痛など日常的な症状の診察',
              },
              { icon: 'ri-syringe-line', title: '予防接種', desc: '各種定期接種・任意接種に対応' },
              { icon: 'ri-heart-add-line', title: '乳幼児健診', desc: '成長発達のチェックと育児相談' },
              { icon: 'ri-lungs-line', title: 'アレルギー疾患', desc: '喘息、アトピー、食物アレルギーの治療' },
              { icon: 'ri-capsule-line', title: '慢性疾患管理', desc: '継続的なケアが必要な疾患のサポート' },
              { icon: 'ri-parent-line', title: '育児相談', desc: '子育ての悩みや不安に寄り添います' },
            ].map((service, index) => (
              <div key={index} className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#F48FB1] to-[#A78BFA] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                  <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-2 sm:mb-3">{service.title}</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Link
              to="/services"
              className="inline-block bg-[#F48FB1] hover:bg-[#EC407A] text-white px-8 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              診療案内の詳細を見る
            </Link>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section id="news" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">お知らせ</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">最新情報をお届けします</p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {newsData.map((news) => (
              <div
                key={news.id}
                className="bg-gradient-to-r from-amber-50/60 to-pink-50/40 p-6 sm:p-8 rounded-3xl shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <span className="text-sm sm:text-base text-gray-500 font-medium whitespace-nowrap">
                      {news.date}
                    </span>
                    <span
                      className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap ${
                        news.category === '重要'
                          ? 'bg-red-500 text-white'
                          : news.category === '休診'
                          ? 'bg-orange-500 text-white'
                          : 'bg-[#F48FB1] text-white'
                      }`}
                    >
                      {news.category}
                    </span>
                  </div>
                  <p className="text-sm sm:text-base text-gray-800 font-medium leading-relaxed">{news.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#EDE7F6]/40 via-[#FCE4EC]/30 to-[#FFFDE7]/40">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">よくあるご質問</h3>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              岡山市の保護者様からよく寄せられる質問
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {faqData.map((faq) => (
              <details key={faq.id} className="bg-white p-6 sm:p-8 rounded-3xl shadow-md hover:shadow-lg transition-all group">
                <summary className="flex items-start gap-3 sm:gap-4 cursor-pointer list-none">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#F48FB1] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-base sm:text-lg">Q</span>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-gray-800 flex-1 leading-relaxed pt-1">{faq.question}</h4>
                  <i className="ri-arrow-down-s-line text-xl sm:text-2xl text-[#F48FB1] group-open:rotate-180 transition-transform flex-shrink-0 mt-1"></i>
                </summary>
                <div className="mt-4 sm:mt-6 flex items-start gap-3 sm:gap-4 pl-0 sm:pl-14">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#A78BFA] rounded-full flex items-center justify-center flex-shrink-0 sm:hidden">
                    <span className="text-white font-bold text-base sm:text-lg">A</span>
                  </div>
                  <div className="hidden sm:flex w-10 h-10 bg-[#A78BFA] rounded-full items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">A</span>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed flex-1">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection />

      <Footer />
    </div>
  );
}
